import React from "react";

import { useTranslation } from "utils";

import { Flex, Icon, Tag, Typography } from "components";

import { BreakdownCardProps, formatScore, getRiskIcon } from "pages/app/resilience-score/utils/ResilienceScoreContextDrawerUtils";
import type { ResilienceTrendDirection } from "pages/app/resilience-score/utils/ResilienceScoreUtils";

const TREND_ICON: Record<ResilienceTrendDirection, string> = {
  up: "tabler/arrow-up-right-circle",
  down: "tabler/arrow-down-right-circle",
  flat: "tabler/arrow-straight-right-circle",
  none: "tabler/arrow-straight-right-circle",
};

const formatPct = (pct: number | null) => {
  if (pct == null || Number.isNaN(pct)) return null;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
};

export const ResilienceScoreBreakdownCard = ({ title, score, maxScore, riskLevel, metricRows, timeline, selectedYear }: BreakdownCardProps) => {
  const { t } = useTranslation(["resilienceScore"]);
  let riskText = null;
  if (riskLevel === "low") {
    riskText = t("resilienceScoreContextDrawer.breakdown.risk.low", "Low Risk");
  } else if (riskLevel === "high") {
    riskText = t("resilienceScoreContextDrawer.breakdown.risk.high", "High Risk");
  }
  const primaryYear = timeline.find((t) => t.year === selectedYear);

  const formatMetricValue = (value: number | null) => {
    const formatted = formatScore(value, "–");
    if (formatted === "–") return formatted;
    return formatted;
  };

  const riskIcon = getRiskIcon(riskLevel);
  return (
    <div className="rsd-breakdown-card">
      <Flex vertical className="rsd-breakdown-card-inner" gap="0.75rem">
        <Flex justify="space-between" align="flex-start" className="rsd-breakdown-top">
          <Flex vertical gap="0.25rem">
            <Typography variant="paragraph-m" bold className="rsd-breakdown-card-title margin-0">
              {title}
            </Typography>
            <Flex align="baseline" gap="0.25rem" className="rsd-breakdown-score">
              <Typography variant="h1" className="rsd-breakdown-score-value margin-0">
                {formatScore(score, "–")}
              </Typography>
              <Typography variant="paragraph-m" className="rsd-breakdown-score-max margin-0">
                /{maxScore}
              </Typography>
            </Flex>
          </Flex>

          {riskText && (
            <Tag
              className="rsd-breakdown-risk-tag"
              colour="yellow"
              size="small"
              text={
                <Flex align="center" gap="0.25rem">
                  <span className="rsd-breakdown-risk-icon">
                    <Icon iconName={riskIcon} size="s" />
                  </span>
                  <Typography variant="paragraph-s" className="margin-0">
                    {riskText}
                  </Typography>
                </Flex>
              }
            />
          )}
        </Flex>

        <div className="rsd-breakdown-metrics">
          {metricRows.map((row) => {
            const pct = formatPct(row.changePct);
            return (
              <Flex key={row.label} justify="space-between" align="center" className="rsd-breakdown-metric-row">
                <Typography variant="paragraph-s" className="margin-0">
                  {row.label}
                </Typography>
                <Flex align="center" gap="0.5rem" className="rsd-breakdown-metric-right">
                  <Typography variant="paragraph-s" className="margin-0 rsd-breakdown-metric-value">
                    {formatMetricValue(row.value)}
                  </Typography>
                  {pct ? (
                    <span className="rsd-breakdown-metric-chip">
                      <Icon iconName={TREND_ICON[row.trend]} size="s" />
                      <span className="rsd-breakdown-metric-chip-text">{pct}</span>
                    </span>
                  ) : (
                    <span className="rsd-breakdown-metric-chip-na">
                      <span className="rsd-breakdown-metric-chip-text">n/a</span>
                    </span>
                  )}
                </Flex>
              </Flex>
            );
          })}
        </div>
      </Flex>
      <div className="rsd-breakdown-mini-timeline">
        {timeline.map((point) => {
          const isCurrent = point.year === selectedYear;
          const heightPct = point.unknown || point.value == null ? 100 : Math.max(0, (point.value / maxScore) * 100);
          return (
            <div key={point.year} className="rsd-mini-col">
              <div className={["rsd-mini-bar", point.unknown ? "unknown" : "", isCurrent ? "current" : ""].filter(Boolean).join(" ")}>
                <div className="rsd-mini-bar-fill" style={{ height: `${heightPct}%` }} />
              </div>
              <div className="rsd-mini-year">{point.year}</div>
              <div className="rsd-mini-value">{point.unknown ? "–" : formatScore(point.value, "–")}</div>
            </div>
          );
        })}
      </div>

      {primaryYear?.unknown ? (
        <Typography variant="paragraph-s" className="rsd-breakdown-footnote margin-0">
          {t("resilienceScoreContextDrawer.breakdown.noDataYet", "No data available yet.")}
        </Typography>
      ) : null}
    </div>
  );
};
